import { useEffect, useState } from "react";

import { RotateCw } from "lucide-react";
import { useDispatch } from "react-redux";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useRetrySummaryGeneration,
  useUpdateFileMetadata,
} from "@/hooks/mutations/use-files";
import { setMessage } from "@/redux/notification";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ConfirmationModal } from "../../ConfirmationModal";

const MAX_LENGTH = 2000;

type EditDocumentDescriptionModalProps = {
  file: DocSorterFileSchema | null;
  isOpen: boolean;
  onClose: () => void;
};

export const EditDocumentDescriptionModal = ({
  file,
  isOpen,
  onClose,
}: EditDocumentDescriptionModalProps) => {
  const dispatch = useDispatch();
  const updateFileMetadata = useUpdateFileMetadata();
  const retrySummaryGeneration = useRetrySummaryGeneration();
  const [description, setDescription] = useState<string>("");
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  useEffect(() => {
    if (file) {
      setDescription(file.short_summary || "");
    }
  }, [file]);

  const isGenerating =
    file?.status === "PROCESSING" || file?.status === "TO_BE_PROCESSED";

  const handleSave = async () => {
    if (!file) return;

    try {
      await updateFileMetadata.mutateAsync({
        fileId: file.id,
        metadata: { short_summary: description },
      });

      dispatch(
        setMessage({
          type: "success",
          content: "Document description updated successfully",
        })
      );
      onClose();
    } catch (_error) {
      dispatch(
        setMessage({
          type: "error",
          content: "Failed to update document description",
        })
      );
    }
  };

  const handleCancel = () => {
    if (file) setDescription(file.short_summary || "");
    onClose();
  };

  const handleRegenerate = () => {
    if (!file) return;

    retrySummaryGeneration.mutate(file.id, {
      onSuccess: () => {
        dispatch(
          setMessage({
            type: "success",
            content: "Description regeneration started",
          })
        );
        setShowRegenerateConfirm(false);
        onClose();
      },
      onError: (error) => {
        dispatch(
          setMessage({
            type: "error",
            content: error.message || "Failed to regenerate description",
          })
        );
      },
    });
  };

  if (!file) return null;

  const charCount = description.length;
  const overLimit = charCount > MAX_LENGTH;
  const isDirty = description !== (file.short_summary || "");

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="grid max-h-[90vh] max-w-2xl grid-rows-[auto_1fr_auto] gap-4 p-0 sm:min-w-[640px]">
          <DialogHeader className="space-y-1 px-6 pb-4 pt-6">
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              Edit Document Description
            </DialogTitle>
            <DialogDescription className="text-sm text-foreground-muted">
              Refine the AI-generated description for{" "}
              <span className="font-medium text-foreground [overflow-wrap:anywhere]">
                {stripBundlePrefix(file.name)}
              </span>{" "}
              or regenerate it from scratch.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 px-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="description" className="text-sm font-medium">
                Description
              </Label>
              <span
                className={cn(
                  "text-xs tabular-nums",
                  overLimit ? "text-destructive" : "text-foreground-muted"
                )}
              >
                {charCount.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
              </span>
            </div>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isGenerating
                  ? "AI is generating the description…"
                  : "Write a short description for this document"
              }
              className="min-h-[200px] resize-none text-sm leading-relaxed"
              disabled={updateFileMetadata.isPending || isGenerating}
              maxLength={MAX_LENGTH}
            />
          </div>

          <DialogFooter className="flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="simple-outline"
              size="sm"
              onClick={() => setShowRegenerateConfirm(true)}
              disabled={
                updateFileMetadata.isPending ||
                retrySummaryGeneration.isPending ||
                isGenerating
              }
            >
              <RotateCw className="size-4" />
              Regenerate with AI
            </Button>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="simple-outline"
                size="sm"
                onClick={handleCancel}
                disabled={updateFileMetadata.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={
                  updateFileMetadata.isPending ||
                  overLimit ||
                  isGenerating ||
                  !isDirty
                }
                isLoading={updateFileMetadata.isPending}
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        type="warning"
        icon={<RotateCw className="size-14 text-destructive" />}
        title="Regenerate Document Description?"
        description="This will replace the current description with a new AI-generated one. The existing text will be lost. Continue?"
        confirmButtonText="Regenerate"
        cancelButtonText="Keep Current"
        onConfirm={handleRegenerate}
        isLoading={retrySummaryGeneration.isPending}
        open={showRegenerateConfirm}
        onOpenChange={setShowRegenerateConfirm}
      />
    </>
  );
};
